import React, { useState } from "react";
import {
  Box,
  Button,
  Modal,
  Typography,
  IconButton,
  Divider,
  alpha,
  useTheme,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CustomInput from "../../FormFields/CustomInput";
import { addMedicine } from "../../../api/services/medication";
import { useQueryClient } from "react-query";
import { useSnackbar } from "notistack";
import { QK } from "../../../base/qk";

const AddMedicineModal = ({ open, handleClose }) => {
  const theme = useTheme();
  const [medicine, setMedicine] = useState({});
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  const handleChange = (e) => {
    setMedicine({ ...medicine, [e.target.name]: e.target.value });
  };

  const handleSubmit = async () => {
    if (!medicine.name) {
      enqueueSnackbar("Medicine Name is required", { variant: "error" });
      return;
    }
    const response = await addMedicine(medicine);
    if (response.status === 201) {
      enqueueSnackbar("Medicine Added", { variant: "success" });
      queryClient.invalidateQueries([QK.medicineLists]);
      queryClient.invalidateQueries([QK.medicineNames]);
      await queryClient.refetchQueries([QK.medicineNames]);
      setMedicine({});
      handleClose();
    } else {
      enqueueSnackbar("Something went wrong. check the data", {
        variant: "error",
      });
    }
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      aria-labelledby="add-medicine-modal"
    >
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: 420,
          bgcolor: "background.paper",
          borderRadius: "16px",
          boxShadow: "0px 24px 48px -12px rgba(16, 24, 40, 0.25)",
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            p: 2.5,
            pb: 1.5,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: "10px",
                bgcolor: alpha(theme.palette.primary.main, 0.08),
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Typography variant="h6" sx={{ color: theme.palette.primary.main, fontWeight: 700 }}>
                💊
              </Typography>
            </Box>
            <Box>
              <Typography variant="h6" fontWeight={600} sx={{ color: "#1A1A2E" }}>
                Add New Medicine
              </Typography>
              <Typography variant="caption" sx={{ color: "#6B7280" }}>
                Add a new medicine to the system
              </Typography>
            </Box>
          </Box>
          <IconButton
            onClick={handleClose}
            size="small"
            sx={{
              color: "#9CA3AF",
              "&:hover": { bgcolor: "#F3F4F6", color: "#4B5563" },
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
        <Divider sx={{ opacity: 0.4 }} />

        <Box sx={{ p: 2.5 }}>
          <CustomInput
            name="name"
            label="Medicine Name"
            value={medicine.name}
            onChange={handleChange}
            required
            fullWidth
          />
          <CustomInput
            name="company"
            label="Company Name"
            value={medicine.company}
            onChange={handleChange}
            fullWidth
            sx={{ mt: 2 }}
          />

          <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1.5, mt: 3 }}>
            <Button
              onClick={handleClose}
              variant="outlined"
              sx={{
                textTransform: "none",
                fontWeight: 500,
                borderRadius: "10px",
                borderColor: "#D0D5DD",
                color: "#344054",
                "&:hover": {
                  bgcolor: "#F9FAFB",
                  borderColor: "#98A2B3",
                },
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              variant="contained"
              sx={{
                textTransform: "none",
                fontWeight: 600,
                borderRadius: "10px",
                bgcolor: "#2469F0",
                px: 4,
                "&:hover": {
                  bgcolor: "#1A5BD4",
                  boxShadow: "0px 4px 12px rgba(36, 105, 240, 0.3)",
                },
              }}
            >
              Add
            </Button>
          </Box>
        </Box>
      </Box>
    </Modal>
  );
};

export default AddMedicineModal;